import { Close, PrintOutlined } from "@mui/icons-material";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DownloadFormat, DownloadFormatButton } from "../../../components/UIToolkit/Clickable/DownloadFormatButton";
import { DateInput } from "../../../components/UIToolkit/Inputs/DateInput";
import { SpreadsheetPreview } from "../../../components/UIToolkit/Previews/SpreadsheetPreview";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { generatePrintoutPdf } from "../../../downloads/downloadDayViewPdf";
import { handleDownload } from "../../../downloads/utils";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import type { DepartmentLocation } from "../../../utils/models";
import { useFacilityScheduleDownload } from "./useFacilityScheduleDownload";
import { useFacilitySchedulePreview } from "./useFacilitySchedulePreview";

/** Snap a date to the preceding (or same) Sunday */
const toSunday = (date: DateTime): DateTime => {
	// Luxon ISO weekday: Mon=1 … Sun=7. Days since Sunday = weekday % 7
	return date.minus({ days: date.weekday % 7 }).startOf("day");
};

/** Snap a date to the following (or same) Saturday */
const toSaturday = (date: DateTime): DateTime => {
	// Sat = weekday 6; days until next Sat from ISO weekday
	const daysToSat = date.weekday === 6 ? 0 : date.weekday === 7 ? 6 : 6 - date.weekday;
	return date.plus({ days: daysToSat }).startOf("day");
};

const DEFAULT_WEEKS = 6;

type Props = {
	location: DepartmentLocation;
};

export const FacilityScheduleExportButton: FC<Props> = ({ location }) => {
	const defaultStart = toSunday(DateTime.now());
	const defaultEnd = toSaturday(defaultStart.plus({ weeks: DEFAULT_WEEKS }).minus({ days: 1 }));

	const [isModalOpen, setIsModalOpen] = useState(false);
	// The PDF is built client-side, so the xlsx mutation's pending flag says nothing about it.
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const [startDate, setStartDate] = useState<DateTime>(defaultStart);
	const [endDate, setEndDate] = useState<DateTime>(defaultEnd);

	const startDateStr = startDate.toFormat("yyyy-MM-dd");
	const endDateStr = endDate.toFormat("yyyy-MM-dd");

	const {
		data: previewSheets = [],
		isFetching: isLoadingPreview,
		error: previewError,
		refetch: refetchPreview,
	} = useFacilitySchedulePreview({
		departmentId: location.department_id,
		locationName: location.location,
		startDate: startDateStr,
		endDate: endDateStr,
		enabled: isModalOpen,
	});

	const handleOpen = () => {
		setStartDate(defaultStart);
		setEndDate(defaultEnd);
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const { mutateAsync: downloadSchedule, isPending: isDownloading } = useFacilityScheduleDownload();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	const handleStartChange = (date: DateTime) => setStartDate(toSunday(date));
	const handleEndChange = (date: DateTime) => setEndDate(toSaturday(date));

	// The xlsx download issues its OWN request, so an empty or failed preview must not block it; only
	// the PDF needs sheets, and the tooltip says which is missing rather than greying out in silence.
	const downloadDisabledReason = isLoadingPreview ? t("preparing.schedule") : undefined;

	const handleDownloadClick = async (format: DownloadFormat) => {
		const baseName = `facility_schedule_${location.location_short_name || location.location}_${startDateStr}`.replace(/\s+/g, "_").toLowerCase();

		// The PDF comes off the sheets already on screen, so it needs no second request — and it is
		// the formatted page users used to chase through Print.
		if (format === "pdf") {
			if (previewSheets.length === 0) {
				showErrorSnackbar(t("no.data.available.to.export"), {}, new Error("facility schedule pdf requested with no preview sheets"));
				return;
			}
			setIsGeneratingPdf(true);
			try {
				// Nothing here is a user choice: this export has no printout panel, so every page-setup argument
				// is null and the workbook's own setup decides. It declares A4 PORTRAIT, fit to one page wide —
				// a hardcoded landscape here used to break the PDF into different columns than the xlsx.
				handleDownload(generatePrintoutPdf(previewSheets, null, null, null, null), `${baseName}.pdf`, "application/pdf");
			} catch (error) {
				showErrorSnackbar(t("download.pdf.failed"), {}, error);
			} finally {
				setIsGeneratingPdf(false);
			}
			return;
		}

		await downloadSchedule({
			departmentId: location.department_id,
			locationName: location.location,
			startDate: startDateStr,
			endDate: endDateStr,
			fileName: `${baseName}.xlsx`,
		});
	};

	return (
		<>
			<SystemButton buttonVariant="secondary" size="small" onClick={handleOpen} leadingIcon={<PrintOutlined />} sx={{ border: "none" }}>
				Export
			</SystemButton>

			<Modal open={isModalOpen} onClose={handleClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", height: "100vh" }}>
				<Box
					sx={{
						bgcolor: "background.paper",
						borderRadius: 2,
						p: 3,
						width: "90vw",
						height: "85vh",
						outline: "none",
						boxShadow: 24,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					{/* Header */}
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Typography variant="h6" sx={{ fontWeight: 600, color: "primary.dark" }}>
							Export Schedule — {location.location}
						</Typography>
						<IconButton onClick={handleClose} size="small">
							<Close />
						</IconButton>
					</Box>

					{/* Date range pickers */}
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						<DateInput label="Start (Sunday)" value={startDate} onChange={handleStartChange} shouldDisableDate={(date) => date.weekday !== 7} />
						<DateInput label="End (Saturday)" value={endDate} onChange={handleEndChange} minDate={startDate} shouldDisableDate={(date) => date.weekday !== 6} />
					</Box>

					{/* Preview */}
					<SpreadsheetPreview
						dataTestId="facility-schedule-preview"
						sheets={previewSheets}
						isLoading={isLoadingPreview}
						// A rejected request also yields zero sheets, so without this a failure was
						// indistinguishable from an empty range and offered no way back.
						emptyLabel={previewError ? t("download.preview.error") : undefined}
						action={
							previewError ? (
								<SystemButton data-testid="facility-schedule-export-retry" buttonVariant="secondary" size="small" onClick={() => void refetchPreview()}>
									{t("retry")}
								</SystemButton>
							) : undefined
						}
						showSheetTabs={false}
						sx={{ flex: 1 }}
					/>

					{/* Actions */}
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
						<DownloadFormatButton
							dataTestIdPrefix="facility-schedule-export"
							formats={["xlsx", "pdf"]}
							onDownload={handleDownloadClick}
							isDownloading={isDownloading || isGeneratingPdf}
							disabled={Boolean(downloadDisabledReason)}
							disabledReason={downloadDisabledReason}
						/>
					</Box>
				</Box>
			</Modal>
		</>
	);
};
